/* Variant media filter
   Works out which media belongs to which variant from the data snippets/variant-media-filter.liquid
   renders, and applies it to a media-gallery. Galleries that manage their own DOM,
   such as product-left-thumbs, use window.VariantMediaFilter.build and apply the
   result themselves, so both share one set of matching rules. */

window.VariantMediaFilter = window.VariantMediaFilter || {
  normalize(value) {
    return String(value == null ? '' : value)
      .trim()
      .toLowerCase();
  },

  /* Alt text: an image whose alt is an option value ("Red") or a full variant
     title ("Red / Large") belongs to the variants that carry it. An alt nobody
     claims, including an empty one, belongs to every variant. */

  buildFromAlt(data) {
    const claimed = new Set();

    data.variants.forEach((variant) => {
      claimed.add(this.normalize(variant.title));
      (variant.options || []).forEach((option) => claimed.add(this.normalize(option)));
    });
    claimed.delete('');

    const isClaimed = (media) => claimed.has(this.normalize(media.alt));
    if (!data.media.some(isClaimed)) return null;

    const groups = new Map();

    data.variants.forEach((variant) => {
      const keys = new Set([
        this.normalize(variant.title),
        ...(variant.options || []).map((option) => this.normalize(option)),
      ]);

      const mediaIds = data.media
        .filter((media) => !isClaimed(media) || keys.has(this.normalize(media.alt)))
        .map((media) => String(media.id));

      groups.set(String(variant.id), new Set(mediaIds));
    });

    return groups;
  },

  /* Order: a variant's own image opens its group, and every image after it up to
     the next variant image joins that group. Images before the first variant
     image belong to every variant. */

  buildFromOrder(data) {
    const ownersByMedia = new Map();

    data.variants.forEach((variant) => {
      if (!variant.featuredMediaId) return;

      const mediaId = String(variant.featuredMediaId);
      if (!ownersByMedia.has(mediaId)) ownersByMedia.set(mediaId, new Set());
      ownersByMedia.get(mediaId).add(String(variant.id));
    });

    if (!ownersByMedia.size) return null;

    const shared = [];
    const ownersOfMedia = new Map();
    let currentOwners = null;

    data.media.forEach((media) => {
      const mediaId = String(media.id);

      if (ownersByMedia.has(mediaId)) currentOwners = ownersByMedia.get(mediaId);

      if (currentOwners) {
        ownersOfMedia.set(mediaId, currentOwners);
      } else {
        shared.push(mediaId);
      }
    });

    const groups = new Map();

    data.variants.forEach((variant) => {
      const variantId = String(variant.id);
      const mediaIds = new Set(shared);

      ownersOfMedia.forEach((owners, mediaId) => {
        if (owners.has(variantId)) mediaIds.add(mediaId);
      });

      groups.set(variantId, mediaIds);
    });

    return groups;
  },

  /**
   * @param {object} data - Parsed contents of the variant media filter script.
   * @returns {Map<string, Set<string>>|null} Media ids per variant id, or null when
   *   the product carries nothing to group media by.
   */
  build(data) {
    if (!data || !Array.isArray(data.media) || !Array.isArray(data.variants)) return null;
    if (!data.media.length || !data.variants.length) return null;

    if (data.mode === 'alt') return this.buildFromAlt(data);
    if (data.mode === 'grouped') return this.buildFromOrder(data);

    return this.buildFromAlt(data) || this.buildFromOrder(data);
  },

  /**
   * Reads the data a gallery carries, if any.
   *
   * @param {HTMLElement} root - Element containing the JSON script.
   * @returns {object|null}
   */
  parse(root) {
    const script = root?.querySelector('[data-variant-media-filter]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  },

  /**
   * The media a variant should show. Anything unknown, or a variant that would be
   * left with no media at all, falls back to showing everything.
   *
   * @param {Map<string, Set<string>>} groups
   * @param {string|number} variantId
   * @returns {Set<string>|null} null means no filtering.
   */
  visibleFor(groups, variantId) {
    const mediaIds = groups?.get(String(variantId));
    return mediaIds && mediaIds.size ? mediaIds : null;
  },
};

/* The media-gallery flavour: slides live in a Swiper, so the filtered row is
   rebuilt from a snapshot of the original slides the way the theme's own grouped
   variant media does, rather than hiding slides the slider would still count. */

if (!customElements.get('variant-media-filter')) {
  class VariantMediaFilterElement extends HTMLElement {
    connectedCallback() {
      this.gallery = this.closest('media-gallery');
      if (!this.gallery) return;

      const data = window.VariantMediaFilter.parse(this);
      this.groups = window.VariantMediaFilter.build(data);
      if (!this.groups) return;

      this.apply(data.selectedVariantId);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const sectionId = this.gallery.dataset.sectionId;
          // Another product section on the same page owns that change.
          if (sectionId && event.data?.sectionId && event.data.sectionId !== sectionId) return;

          this.apply(event.data?.variant?.id);
        });
      }
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    apply(variantId) {
      const visible = window.VariantMediaFilter.visibleFor(this.groups, variantId);

      this.filterCarousel(visible);
      this.filterGrid(visible);
      this.filterZoomDialog(visible);

      const first = this.gallery.querySelector(
        '.swiper__slides [data-media-id], .media-gallery__grid-item[data-media-id]:not(.hidden)'
      );

      if (first) this.gallery.setActiveMedia?.(first.dataset.mediaId, false);
    }

    /* The original slides, kept so every filter is applied to the full set rather
       than to what the previous variant left behind. */

    snapshot() {
      if (this.snapshotData) return this.snapshotData;

      const swiperComponent = this.gallery.querySelector('swiper-component');
      const slidesWrapper = swiperComponent?.querySelector('.swiper-wrapper.swiper__slides');
      const thumbsWrapper = this.gallery.querySelector(
        '.swiper-controls__thumbnails-container .swiper-wrapper'
      );

      this.snapshotData = {
        swiperComponent,
        slidesWrapper,
        thumbsWrapper,
        slides: slidesWrapper ? Array.from(slidesWrapper.children) : [],
        thumbs: thumbsWrapper ? Array.from(thumbsWrapper.children) : [],
      };

      return this.snapshotData;
    }

    filterCarousel(visible) {
      const snapshot = this.snapshot();
      if (!snapshot.slidesWrapper) return;

      // Controls without a media id, such as the autoplay buttons, always stay.
      const keep = (node) => !node.dataset.mediaId || !visible || visible.has(node.dataset.mediaId);

      const slides = snapshot.slides.filter(keep);
      if (!slides.length) return;

      const hadSwiper = Boolean(snapshot.swiperComponent?.initSwiper);
      if (hadSwiper) this.destroySwipers(snapshot.swiperComponent);

      snapshot.slidesWrapper.replaceChildren(...slides.map((node) => node.cloneNode(true)));

      if (snapshot.thumbsWrapper) {
        const thumbs = snapshot.thumbs.filter(keep);
        snapshot.thumbsWrapper.replaceChildren(...thumbs.map((node) => node.cloneNode(true)));
      }

      // The component caches its slide list the first time it is read.
      if (snapshot.swiperComponent) snapshot.swiperComponent._items = null;

      // Before it has initialised, the lazy init picks up the filtered row on its own.
      if (hadSwiper) snapshot.swiperComponent.initializeSwiper?.();
    }

    destroySwipers(swiperComponent) {
      try {
        swiperComponent.initSwiper?.destroy?.(true, true);
        swiperComponent.initSwiper = null;

        const thumbsContainer = this.gallery.querySelector(
          '.swiper-controls__thumbnails-container .swiper'
        );
        const thumbsSwiper = thumbsContainer?.swiper || swiperComponent._thumbsSwiper;

        thumbsSwiper?.destroy?.(true, true);
        swiperComponent._thumbsSwiper = null;

        if (swiperComponent.swiperEl) swiperComponent.swiperEl._swiperInitialized = false;
        if (thumbsContainer) thumbsContainer._swiperInitialized = false;
      } catch (error) {
        // A slider that refuses to tear down must not stop the gallery rendering.
      }
    }

    filterGrid(visible) {
      this.gallery.querySelectorAll('.media-gallery__grid-item[data-media-id]').forEach((item) => {
        item.classList.toggle('hidden', Boolean(visible) && !visible.has(item.dataset.mediaId));
      });
    }

    filterZoomDialog(visible) {
      const dialog = this.gallery.querySelector('zoom-dialog');
      if (!dialog) return;

      let firstVisibleIndex = null;

      dialog.querySelectorAll('.dialog-zoomed-gallery [data-media-id]').forEach((item, index) => {
        const show = !visible || visible.has(item.dataset.mediaId);

        item.classList.toggle('hidden', !show);
        if (show && firstVisibleIndex === null) firstVisibleIndex = index;
      });

      dialog
        .querySelectorAll('.dialog-thumbnails-list__thumbnail[data-media-id]')
        .forEach((button) => {
          const show = !visible || visible.has(button.dataset.mediaId);

          button.classList.toggle('hidden', !show);
          button.setAttribute('tabindex', show ? '0' : '-1');
        });

      if (firstVisibleIndex !== null) {
        dialog.selectThumbnail?.(firstVisibleIndex, { behavior: 'auto' });
      }
    }
  }

  customElements.define('variant-media-filter', VariantMediaFilterElement);
}
